import React, { Fragment, useState, useEffect, useRef } from 'react';
import { iecWiseBrcDetailsService } from '../../../services/BrcReportService';
import { getBRCorelationService, getReferenceDataService } from '../../../services/MasterService';
import { Table, Button, Spin, message, DatePicker, Select } from 'antd';
import { Col, Row } from 'react-bootstrap';
import { Excel } from 'antd-table-saveas-excel';
import Card from '../../../components/bootstrap/card';
import { getAdvAuthLedgerDataService } from '../../../services/BOEReportService';
import { getReportSummaryData, getAllAdminIECsService, GetLicenseNoService } from '../../../services/FileDataService';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;
const AdvAuthLedgerReport = ({ data }) => {
  const location = useLocation();
  const [jsonData, setJsonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [error, setError] = useState(null);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const [billNoOptions, setBillNoOptions] = useState([]); // Options for dropdown
  const [selectedBillNos, setSelectedBillNos] = useState([]); // Selected auth numbers
  const [isValid, setIsValid] = useState(true); // Validation state
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  const today = new Date();
  // Extract day, month, and year components
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
  const year = today.getFullYear();
  const reportType = 'BRC';
  // Concatenate components with hyphens
  const formattedDate = `${day}-${month}-${year}`;
  // ─── TABLE STYLES ────────────────────────────────────────────────────────────
  const tableStyles = `
    .adv-auth-summary-table .ant-table-thead > tr > th,
    .adv-auth-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .adv-auth-summary-table .ant-table-tbody > tr > td,
    .adv-auth-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .adv-auth-summary-table .ant-table-container,
    .adv-auth-table .ant-table-container {
      overflow-x: auto !important;
    }
    .adv-auth-summary-table .ant-table-header,
    .adv-auth-table .ant-table-header {
      overflow: hidden !important;
    }
    .adv-auth-summary-table .ant-table-sticky-holder,
    .adv-auth-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  // Define columns for the table
  const columns = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', header: 'BUSINESS UNIT', width: 160 , fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', header: 'PLANT NAME', width: 150 , fixed: 'left' },
    {
      title: 'ADV. AUTH. NO.',
      dataIndex: 'auth_number',
      key: 'auth_number',
      header: 'ADV. AUTH. NO.',
      width: 160,
    },
    {
      title: 'ADV. AUTH. DT.',
      dataIndex: 'import_date',
      key: 'import_date',
      header: 'ADV. AUTH. DT.',
      width: 170,
      render: (text, record) => {
        if (record.import_date) {
          return formatDateToDDMMYY(record.import_date);
        }
      }
    },
    {
      title: 'IMPORT ITEM SR. NO. AS PER LIC.',
      dataIndex: 'import_sr_no',
      key: 'import_sr_no',
      header: 'IMPORT ITEM SR. NO. AS PER LIC.',
      align: 'right',
      width: 320,
    },
    {
      title: 'AUTH. DESCRIPTION',
      dataIndex: 'auth_description',
      key: 'auth_description',
      header: 'AUTH. DESCRIPTION',
      width: 200,
    },
    {
      title: 'ATHO. QTY.',
      dataIndex: 'quantity',
      key: 'quantity',
      header: 'ATHO. QTY.',
      align: 'right',
      width: 140,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'ATHO. UOM.',
      dataIndex: 'uom',
      key: 'uom',
      header: 'ATHO. UOM.',
      width: 140,
    },
    {
      title: 'IEC/BR CODE',
      dataIndex: 'iec_br',
      key: 'iec_br',
      header: 'IEC/BR CODE',
      width: 150,
    },
    {
      title: 'PORT',
      dataIndex: 'port',
      key: 'port',
      header: 'PORT',
      width: 100,
    },
    {
      title: 'REF. CODE',
      dataIndex: 'reference_code',
      key: 'reference_code',
      header: 'REF. CODE',
      width: 125,
    },
    {
      title: 'BOE NO.',
      dataIndex: 'be_no',
      key: 'be_no',
      header: 'BOE NO.',
      width: 120,
    },
    {
      title: 'BOE DTD.',
      dataIndex: 'be_date',
      key: 'be_date',
      header: 'BOE DTD.',
      width: 120,
      render: (text, record) => {
        if (record.be_date) {
          return formatDateToDDMMYY(record.be_date);
        }

      }
    },
    {
      title: 'BOE QTY',
      dataIndex: 'qty',
      key: 'qty',
      header: 'BOE QTY',
      align: 'right',
      width: 120,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('AdvanceAuthLedgerReport', columns);

  const dashboardReportSummaryColumns = [
    {
      title: 'ADV. AUTH. NO.',
      dataIndex: 'auth_number',
      key: 'auth_number',
      header: 'ADV. AUTH. NO.',
      width: 160,
    },
    {
      title: 'ADV. AUTH. DT.',
      dataIndex: 'import_date',
      key: 'import_date',
      header: 'ADV. AUTH. DT.',
      width: 170,
    },
    {
      title: 'IMPORT ITEM SR. NO. AS PER LIC.',
      dataIndex: 'import_sr_no',
      key: 'import_sr_no',
      header: 'IMPORT ITEM SR. NO. AS PER LIC.',
      align: 'right',
      width: 320,
    },
    {
      title: 'AUTH. DESCRIPTION',
      dataIndex: 'auth_description',
      key: 'auth_description',
      header: 'AUTH. DESCRIPTION',
      width: 200,
    },
    {
      title: 'ATHO. QTY.',
      dataIndex: 'auth_qty',
      key: 'auth_qty',
      header: 'ATHO. QTY.',
      align: 'right',
      width: 140,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'ATHO. UOM.',
      dataIndex: 'auth_uom',
      key: 'auth_uom',
      header: 'ATHO. UOM.',
      width: 140,
    },
    {
      title: 'TOL. QTY. IMPORTED',
      dataIndex: 'total_qty',
      key: 'total_qty',
      header: 'TOL. QTY. IMPORTED',
      align: 'right',
      width: 205,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BAL. QTY.',
      dataIndex: 'bal_qty',
      key: 'bal_qty',
      header: 'BAL. QTY.',
      align: 'right',
      width: 140,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'VALID TILL',
      dataIndex: 'import_validity',
      key: 'import_validity',
      header: 'VALID TILL',
      width: 140,
    },
    {
      title: 'VALID FOR (DAYS)',
      dataIndex: 'valid_for',
      key: 'valid_for',
      header: 'VALID FOR (DAYS)',
      align: 'right',
      width: 180,
    },
  ];

  const getSummaryDashboard = async () => {
    const filters = {
      bill_no: selectedBillNos.length > 0 ? selectedBillNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      setIsLoading2(true)
      let reportTypes = 'AdvAuth-ScripLedger';
      const response = await getReportSummaryData(reportTypes, filters, selectedIEC);

      if (response) {
        console.log("response: ", response.data);
        if (response.data) {
          setSummaryDashboardData(response.data);
        }
      }
      else {
        console.log("getSummaryDashboard :: no reponse")
      }
      setIsLoading2(false)
    }
    catch (err) {
      setIsLoading2(false)
      console.log(err);
    }
  }
  const fetchData = async () => {
    const filters = {
      bill_no: selectedBillNos.length > 0 ? selectedBillNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      const response = await getAdvAuthLedgerDataService(selectedIEC, filters);

      setJsonData(response);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching data:', error);
      message.error(`Error fetching data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch license numbers immediately for the selected IEC
  const fetchLicenseNumbers = async (iec) => {
    const targetIEC = iec || selectedIEC;
    if (!targetIEC) return;
    try {
      const token = localStorage.getItem('token');
      const res = await GetLicenseNoService(token, targetIEC, 'ADV_AUTH');
      const list = res?.data || (Array.isArray(res) ? res : []);
      if (list.length > 0) {
        const options = list.map((item, index) => ({
          value: typeof item === 'object' ? (item.license_number || item.auth_number) : String(item),
          label: typeof item === 'object' ? (item.license_number || item.auth_number) : String(item),
          key: index,
        }));
        setBillNoOptions(options);
      }
    } catch (error) {
      console.error('Error fetching license numbers:', error);
    }
  };

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchLicenseNumbers(selectedIEC);
      if (selectedIEC) {
        getBRCorelationService(selectedIEC).then((response) => {
          setBranchCodeNames(response);
        });
      }
    }

  }, []);

  // Fetch license numbers when IEC changes
  useEffect(() => {
    if (selectedIEC) {
      setBillNoOptions([]);
      setSelectedBillNos([]);
      fetchLicenseNumbers(selectedIEC);
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, [selectedIEC]);

  const preprocessData = (data) => {
    return data.map(record => {
      if (record.sb_date) {
        const sbDate = new Date(record.sb_date);
        // Extract fiscal year from sb_date
        const fiscalYearStartYear = sbDate.getMonth() >= 3 ? sbDate.getFullYear() : sbDate.getFullYear() - 1;
        const fiscalYearEndYear = fiscalYearStartYear + 1;
        const fiscalYear = `AM${fiscalYearEndYear.toString().slice(-2)}`;
        record.FY = fiscalYear;
        // Format sb_date

        record.sb_date = formatDateToDDMMYY(sbDate);
      } else {
        record.FY = '';
        record.sb_date = '';
        record.sbDate = '';
      }
      return record;
    });
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
      setBillNoOptions([]);
      setSelectedBillNos([]);
    }
  };

  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the dateString is not a valid date
    }
    else {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    }

  }

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }
  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };
  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }
    getSummaryDashboard();
    fetchData();

    // if (dates.length === 2 && isValid) {
    //   getSummaryDashboard();
    //   fetchData();
    // }
    // else {
    //   message.error("Please select a valid date range.");
    // }
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async () => {
    let workbook = new ExcelJS.Workbook();
    let summaryWorksheet = workbook.addWorksheet('Summary');
    let worksheet = workbook.addWorksheet('Data');
    var data = jsonData;

    // Define columns for Excel export
    summaryWorksheet.columns = filteredSummaryColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    summaryWorksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    summaryDashboardData.forEach((obj) => {
      summaryWorksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
        import_validity: getDateOnly(obj.import_validity),
        auth_qty: parseFloat(obj.auth_qty),
        bal_qty: obj.bal_qty != '' ? parseFloat(obj.bal_qty) : null
      })
    })

    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
        be_date: getDateOnly(obj.be_date),
        qty: obj.qty ? parseFloat(obj.qty) : '',
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    saveAs(blob, `Lucrative's CIP DSS - Adv. Auth. Ledger as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
  };

  const filteredSummaryColumns = dashboardReportSummaryColumns.filter((col) => {
    const key = String(col.dataIndex || col.key);
    const isDefinedInDetail = columns.some((c) => String(c.dataIndex || c.key) === key);
    return isDefinedInDetail ? visibleKeys.includes(key) : true;
  });

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">Lucrative's CIP DSS  -  Adv. Auth. Ledger as on {formattedDate} of {selectedIECName}</h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button
                  className="btn btn-primary cip-btn-submit"
                  onClick={selectedBillNos.length === 0 ? undefined : exportToExcel}
                  style={{ cursor: (loading || selectedBillNos.length === 0) ? 'not-allowed' : 'pointer' }}
                >
                  Export to Excel
                </button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              {/* <DatePicker.RangePicker value={dates} onChange={handleDateChange}
                allowClear
              /> */}
              <Select
                mode="multiple"
                showSearch
                placeholder="Select Auth No(s)"
                value={selectedBillNos}
                allowClear
                style={{ width: 250 }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(vals) => setSelectedBillNos(vals || [])} // Update state when option(s) are selected
                options={billNoOptions} // Dynamically populated options
                disabled={!selectedIEC}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                style={{ cursor: selectedBillNos.length === 0 ? 'not-allowed' : 'pointer' }}
                onClick={selectedBillNos.length === 0 ? undefined : handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              <Table
                className="adv-auth-summary-table"
                columns={filteredSummaryColumns}
                dataSource={summaryDashboardData}
                bordered
                // pagination={false}
                loading={isLoading2}
                scroll={{ x: 'max-content' }}
                
              />

              <hr />
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="large" />
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Error loading data: {error}</p>
                </div>
              ) : (
                <Table
                  className="adv-auth-table"
                  bordered
                  columns={filteredColumns}
                  dataSource={jsonData}
                  pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AdvAuthLedgerReport;
